import React, { useMemo, useState } from 'react';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/arrow-right-icon.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow-down-icon.svg';
import { Dropdown, DropdownItem, DropdownList } from '@cokitchen/cokitchen-components';
import { CalendarAltIcon } from 'cokitchen-icons';
import moment from 'moment';
import useWeeklyRosterContext from 'contexts/weekly-roster.context';

export type WeekType = {
  start: moment.Moment;
  end: moment.Moment;
};

type Props = {
  value: WeekType | null;
  onChange: (v: WeekType) => void;
  isForm?: boolean;
};

const MonthSelector = (props: Props) => {
  const { value, onChange, isForm } = props;
  const [open, setOpen] = useState(false);
  const { year, setYear } = useWeeklyRosterContext();

  const incrementYear = () => {
    if (year < moment().year()) setYear(year + 1);
  };

  const decrementYear = () => {
    setYear(year - 1);
  };
  const weeksInYear = useMemo(() => {
    const weeks: { start: moment.Moment; end: moment.Moment }[] = [];
    const firstWeekStart = moment(String(year)).startOf('year').startOf('week');
    const lastWeekEnd = moment(String(year)).endOf('year').endOf('week');

    const current = firstWeekStart.clone();

    while (current.isSameOrBefore(lastWeekEnd)) {
      const start = current.clone();
      const end = current.clone().add(6, 'days');

      weeks.push({ start, end });
      current.add(7, 'days');
    }

    return weeks;
  }, [year]);

  return (
    <div>
      {isForm ? (
        <div
          onClick={() => setOpen(!open)}
          className='flex items-center justify-between cursor-pointer py-3 px-4 border border-[#E2E3E4] rounded-[8px]'>
          {value ? (
            <p className='flex items-center gap-1 text-sm'>
              <span>{value.start.format('Do MMM, YYYY')}</span>
              <ArrowRightIcon />
              <span>{value.end.format('Do MMM, YYYY')}</span>
            </p>
          ) : (
            <p className='text-[#A6A6A6] text-sm'>Select week</p>
          )}
          <ArrowDownIcon />
        </div>
      ) : (
        <div
          className='flex items-center gap-10 px-3 py-2 border border-[#E5E6E6] rounded-[6px] hover:bg-[#E5E6E650] cursor-pointer user-select-none'
          onClick={() => setOpen(!open)}>
          <span className='flex items-center gap-1'>
            <p className='text-[13px] leading-[20.8px] text-[#5E6164]'>
              {value?.start.format('Do MMM, YYYY')}
            </p>
            <ArrowRightIcon />
            <p className='text-[13px] leading-[20.8px] text-[#5E6164]'>
              {value?.end.format('Do MMM, YYYY')}
            </p>
          </span>
          <CalendarAltIcon className='[&_path]:fill-[#96999C]' />
        </div>
      )}

      <Dropdown visible={open} onClickOutside={() => setOpen(false)}>
        <DropdownList className='min-w-[400px] flex flex-col gap-1 p-0 pb-1 rounded-[6px]'>
          <div className='flex items-center justify-between gap-2 px-4 py-[10px] bg-[#F5F5F5]'>
            <p>{year}</p>
            <div className='flex items-center gap-[10px]'>
              <ArrowRightIcon
                className='rotate-180 [&_path]:fill-[#858585] cursor-pointer'
                onClick={decrementYear}
              />
              <ArrowRightIcon
                className={`cursor-pointer ${
                  year === moment().year() ? '[&_path]:fill-[#DEDEDE]' : '[&_path]:fill-[#858585]'
                }`}
                onClick={incrementYear}
              />
            </div>
          </div>
          {weeksInYear.map((week, i) => (
            <DropdownItem
              key={i}
              className='user-select-none'
              onClick={() => {
                onChange(week);
                setOpen(false);
              }}>
              <div className='w-full flex items-center justify-between gap-2'>
                <p className='text-sm'>{`Week ${i + 1} (${week.start.format(
                  'Do MMM, YYYY'
                )} - ${week.end.format('Do MMM, YYYY')})`}</p>
              </div>
            </DropdownItem>
          ))}
        </DropdownList>
      </Dropdown>
    </div>
  );
};

export default MonthSelector;
